import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Select,
  Tooltip,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { MdDelete, MdPersonAdd, MdOutlineEmail } from "react-icons/md";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import InviteAgencyStaffModal from "./InviteAgencyStaffModal";

const ROLE_COLORS = { owner: "purple", admin: "blue", member: "green", viewer: "gray" };

export default function AgencyStaffManagement() {
  const baseUrl = getHostDomain();
  const { agencyData, staffRole } = useContext(AgencyContext);
  const agencyId = agencyData?.id;
  const isOwner = staffRole == null;

  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchStaff = async () => {
    if (!agencyId) return;
    try {
      setLoadingStaff(true);
      const res = await fetchController(
        baseUrl + `/api/v1/agency/${agencyId}/staff`,
        "GET"
      );
      setMembers(res.data?.members || []);
      setPendingInvites(res.data?.pending_invites || []);
    } catch {
      toast.error("Failed to load staff");
    } finally {
      setLoadingStaff(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [agencyId]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyId}/staff/${userId}/role`,
        "PUT",
        { role: newRole }
      );
      toast.success("Role updated");
      fetchStaff();
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyId}/staff/${userId}`,
        "DELETE"
      );
      toast.success("Staff member removed");
      fetchStaff();
    } catch {
      toast.error("Failed to remove member");
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyId}/staff/invite/${inviteId}`,
        "DELETE"
      );
      toast.success("Invite cancelled");
      fetchStaff();
    } catch {
      toast.error("Failed to cancel invite");
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const handleResendInvite = async (inviteId) => {
    try {
      setResendingId(inviteId);
      await fetchController(
        baseUrl + `/api/v1/agency/${agencyId}/staff/invite/${inviteId}/resend`,
        "POST"
      );
      toast.success("Invite resent");
      fetchStaff();
    } catch {
      toast.error("Failed to resend invite");
    } finally {
      setResendingId(null);
    }
  };

  if (loadingStaff) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box>
      {/* Intro / how-it-works */}
      <Box mb={5}>
        <Heading size="md" mb={2}>
          Staff
        </Heading>
        <Text fontSize="sm" color="gray.600" mb={3}>
          Invite teammates to help manage your agency. Each invite goes
          to an email link that expires after a few days; recipients
          accept to join and are added below. Owners control roles
          and can remove members at any time.
        </Text>
        <Alert status="info" borderRadius="md" variant="left-accent">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            <Text mb={1}>
              <Badge colorScheme="purple" mr={2}>Owner</Badge>
              Full control. Can invite, change roles, remove staff,
              and edit billing.
            </Text>
            <Text mb={1}>
              <Badge colorScheme="blue" mr={2}>Admin</Badge>
              Full configuration access except changing the owner or
              billing method.
            </Text>
            <Text mb={1}>
              <Badge colorScheme="green" mr={2}>Member</Badge>
              Can manage subaccounts, contacts, and day-to-day
              operations — but not agency-level settings.
            </Text>
            <Text>
              <Badge colorScheme="gray" mr={2}>Viewer</Badge>
              Read-only access. Useful for support staff or
              stakeholders who need visibility.
            </Text>
          </AlertDescription>
        </Alert>
      </Box>

      <Flex justify="space-between" align="center" mb={4}>
        <HStack>
          <Heading size="sm">Staff members</Heading>
          <Badge variant="subtle" colorScheme="gray">
            {members.length}
          </Badge>
        </HStack>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<MdPersonAdd />}
          onClick={onOpen}
        >
          Invite staff
        </Button>
      </Flex>

      <Table size="sm" mb={8}>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Role</Th>
            {isOwner && <Th>Actions</Th>}
          </Tr>
        </Thead>
        <Tbody>
          {members.length === 0 ? (
            <Tr>
              <Td colSpan={isOwner ? 4 : 3}>
                <Text color="gray.500" fontSize="sm">No staff members yet.</Text>
              </Td>
            </Tr>
          ) : (
            members.map((m) => (
              <Tr key={m.user_id}>
                <Td>{[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}</Td>
                <Td>{m.email}</Td>
                <Td>
                  {isOwner && m.role !== "owner" ? (
                    <Select
                      size="xs"
                      width="120px"
                      value={m.role}
                      onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </Select>
                  ) : (
                    <Badge colorScheme={ROLE_COLORS[m.role] || "gray"}>{m.role}</Badge>
                  )}
                </Td>
                {isOwner && (
                  <Td>
                    {m.role !== "owner" && (
                      <IconButton
                        size="xs"
                        icon={<MdDelete />}
                        colorScheme="red"
                        variant="ghost"
                        aria-label="Remove"
                        onClick={() => handleRemoveMember(m.user_id)}
                      />
                    )}
                  </Td>
                )}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      {pendingInvites.length > 0 && (
        <>
          <HStack mb={3}>
            <Heading size="sm">Pending invites</Heading>
            <Badge variant="subtle" colorScheme="yellow">
              {pendingInvites.length}
            </Badge>
          </HStack>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Expires</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pendingInvites.map((inv) => (
                <Tr key={inv.id}>
                  <Td>{inv.invitee_email}</Td>
                  <Td>
                    <Badge colorScheme={ROLE_COLORS[inv.role] || "gray"}>{inv.role}</Badge>
                  </Td>
                  <Td fontSize="xs">{new Date(inv.expires_at).toLocaleDateString()}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="Resend invite email">
                        <IconButton
                          size="xs"
                          icon={<MdOutlineEmail />}
                          colorScheme="blue"
                          variant="ghost"
                          aria-label="Resend invite"
                          isLoading={resendingId === inv.id}
                          onClick={() => handleResendInvite(inv.id)}
                        />
                      </Tooltip>
                      <Tooltip label="Cancel invite">
                        <IconButton
                          size="xs"
                          icon={<MdDelete />}
                          colorScheme="red"
                          variant="ghost"
                          aria-label="Cancel invite"
                          onClick={() => handleCancelInvite(inv.id)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
      )}

      {isOpen && (
        <InviteAgencyStaffModal
          isOpen={isOpen}
          onClose={onClose}
          agencyId={agencyId}
          onInvited={fetchStaff}
        />
      )}
    </Box>
  );
}
